import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  Tooltip,
  Grid,
  LinearProgress,
  Stack,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CategoryOutlined as CategoryIcon,
  FolderOpen as FolderOpenIcon,
  ChevronRight as ChevronRightIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import Layout from '../../components/layout/Layout';
import categoryService from '../../services/categoryService';
import { getCurrentUser } from '../../services/authService';
import PageTitle from '../../components/PageTitle';
import { downloadXlsxFromRows } from '../../utils/inventoryListExport';

const TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    py: 0.65,
    px: 1.5,
    fontSize: '0.8125rem',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& .MuiTableCell-head': {
    py: 0.85,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'text.secondary',
    bgcolor: 'grey.50',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableRow-root:hover': {
    bgcolor: 'action.hover',
  },
};

/**
 * Categories management page component
 * @returns {JSX.Element} Categories page
 */
const CategoriesPage = () => {
  const user = getCurrentUser();
  const categoryNameInputRef = useRef(null);
  const companyId = user?.companyId || 1;

  const [rawCategoryRows, setRawCategoryRows] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [parentCategories, setParentCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
    description: '',
    parentCategoryId: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'All Categories' }]);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      const allCategories = await categoryService.getCategoriesByCompany(companyId);
      setParentCategories(allCategories);

      if (currentCategory) {
        const data = await categoryService.getSubcategories(currentCategory.id);
        setRawCategoryRows(Array.isArray(data) ? data : []);
      } else {
        setRawCategoryRows(Array.isArray(allCategories) ? allCategories : []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load categories',
        severity: 'error',
      });
      setRawCategoryRows([]);
    } finally {
      setLoading(false);
    }
  }, [currentCategory, companyId]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, currentCategory]);

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredCategories = useMemo(() => {
    if (!normalizedSearch) return rawCategoryRows;
    return rawCategoryRows.filter((c) => {
      const parent = c.parentCategory?.name || '';
      return (
        (c.name || '').toLowerCase().includes(normalizedSearch) ||
        (c.description || '').toLowerCase().includes(normalizedSearch) ||
        parent.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [rawCategoryRows, normalizedSearch]);

  const displayedCategories = useMemo(
    () => filteredCategories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredCategories, page, rowsPerPage]
  );

  const filteredCount = filteredCategories.length;

  const handleExportCategoriesExcel = useCallback(() => {
    const rows = filteredCategories.map((c) => ({
      ID: c.id,
      Name: c.name || '',
      Description: c.description || '',
      'Parent category': c.parentCategory?.name || '',
    }));
    const ok = downloadXlsxFromRows(
      `categories_${new Date().toISOString().split('T')[0]}.xlsx`,
      'Categories',
      rows
    );
    if (!ok) {
      setSnackbar({ open: true, message: 'Nothing to export', severity: 'info' });
    }
  }, [filteredCategories]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setSelectedCategory(category);
      setFormValues({
        name: category.name,
        description: category.description || '',
        parentCategoryId: category.parentCategory?.id || '',
      });
    } else {
      setSelectedCategory(null);
      setFormValues({
        name: '',
        description: '',
        parentCategoryId: currentCategory ? currentCategory.id : '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCategory(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const handleSubmit = async () => {
    try {
      const categoryData = {
        name: formValues.name,
        description: formValues.description || null,
        parentCategoryId: formValues.parentCategoryId || null,
        companyId,
        // Keep existing image on server when editing; no image UI
        imageUrl: selectedCategory?.imageUrl || null,
      };

      if (selectedCategory) {
        await categoryService.updateCategory(selectedCategory.id, categoryData);
        setSnackbar({
          open: true,
          message: 'Category updated successfully',
          severity: 'success',
        });
      } else {
        await categoryService.createCategory({ ...categoryData, imageUrl: null });
        setSnackbar({
          open: true,
          message: 'Category created successfully',
          severity: 'success',
        });
      }

      handleCloseDialog();
      loadCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${selectedCategory ? 'update' : 'create'} category: ${error.response?.data?.message || error.message}`,
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  const handleDeleteCategory = async () => {
    try {
      await categoryService.deleteCategory(categoryToDelete.id);
      setSnackbar({
        open: true,
        message: 'Category deleted successfully',
        severity: 'success',
      });
      handleCloseDeleteDialog();
      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      setSnackbar({
        open: true,
        message: `Failed to delete category: ${error.response?.data?.message || error.message}`,
        severity: 'error',
      });
    }
  };

  const handleNavigateToSubcategories = (category) => {
    setCurrentCategory(category);
    setBreadcrumbs([...breadcrumbs, { id: category.id, name: category.name }]);
  };

  const handleBreadcrumbClick = (breadcrumb, index) => {
    if (index === 0) {
      setCurrentCategory(null);
      setBreadcrumbs([{ id: null, name: 'All Categories' }]);
    } else {
      const category = breadcrumbs[index];
      setCurrentCategory({ id: category.id, name: category.name });
      setBreadcrumbs(breadcrumbs.slice(0, index + 1));
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  return (
    <Layout>
      <PageTitle title="Categories" />
      <Box sx={{ width: '100%', px: { xs: 1.5, sm: 2, md: 3 }, py: 1.5, bgcolor: '#fafbfc', minHeight: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 1.5,
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              Product Categories
            </Typography>
            <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ mt: 0.5 }}>
              {breadcrumbs.map((breadcrumb, index) => (
                <Link
                  key={index}
                  color="inherit"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                  onClick={() => handleBreadcrumbClick(breadcrumb, index)}
                >
                  {index === 0 ? (
                    <FolderOpenIcon sx={{ mr: 0.5 }} fontSize="small" />
                  ) : (
                    <CategoryIcon sx={{ mr: 0.5 }} fontSize="small" />
                  )}
                  {breadcrumb.name}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}>
            <TextField
              size="small"
              placeholder="Search name, description, parent…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: '100%', sm: 260 }, flex: { sm: 1 } }}
            />
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCategoriesExcel}
            >
              Export Excel
            </Button>
            <Button variant="contained" color="primary" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              Add Category
            </Button>
          </Stack>
        </Box>

        {currentCategory && (
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                const newBreadcrumbs = [...breadcrumbs];
                newBreadcrumbs.pop();
                setBreadcrumbs(newBreadcrumbs);
                const parentCategory =
                  newBreadcrumbs.length > 1
                    ? {
                        id: newBreadcrumbs[newBreadcrumbs.length - 1].id,
                        name: newBreadcrumbs[newBreadcrumbs.length - 1].name,
                      }
                    : null;
                setCurrentCategory(parentCategory);
              }}
            >
              Back
            </Button>
            <Typography variant="body2" color="text.secondary">
              Subcategories of <strong>{currentCategory.name}</strong>
            </Typography>
          </Box>
        )}

        <Box sx={{ width: '100%', maxWidth: '95%', mx: 'auto' }}>
          <Paper variant="outlined" elevation={0} sx={{ borderRadius: 1, overflow: 'hidden' }}>
            {loading && <LinearProgress />}
            <TableContainer>
            <Table size="small" sx={TABLE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell width="28%">Name</TableCell>
                  <TableCell width="38%">Description</TableCell>
                  <TableCell width="22%">Parent</TableCell>
                  <TableCell align="center" width="12%">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && rawCategoryRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : displayedCategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      No categories found
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedCategories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                          <CategoryIcon sx={{ color: 'primary.main', fontSize: 20, flexShrink: 0 }} />
                          <Typography variant="body2" fontWeight={600} noWrap title={category.name}>
                            {category.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" noWrap title={category.description || ''}>
                          {category.description?.trim() ? category.description : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {category.parentCategory ? (
                          <Chip label={category.parentCategory.name} size="small" variant="outlined" sx={{ height: 24, fontSize: '0.75rem' }} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Subcategories">
                          <IconButton size="small" color="primary" onClick={() => handleNavigateToSubcategories(category)}>
                            <FolderOpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" color="info" onClick={() => handleOpenDialog(category)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(category)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            size="small"
          />
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: () => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                categoryNameInputRef.current?.focus({ preventScroll: true });
              });
            });
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>{selectedCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                inputRef={categoryNameInputRef}
                fullWidth
                size="small"
                label="Category name"
                name="name"
                value={formValues.name}
                onChange={handleInputChange}
                required
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description (optional)"
                name="description"
                value={formValues.description}
                onChange={handleInputChange}
                multiline
                minRows={2}
                placeholder="Short note for staff"
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Parent category</InputLabel>
                <Select
                  name="parentCategoryId"
                  value={formValues.parentCategoryId}
                  onChange={handleInputChange}
                  label="Parent category"
                >
                  <MenuItem value="">
                    <em>None (root)</em>
                  </MenuItem>
                  {parentCategories
                    .filter((c) => selectedCategory?.id !== c.id)
                    .map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={!formValues.name?.trim()}>
            {selectedCategory ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Delete category?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{categoryToDelete?.name}</strong>? This cannot be undone.
          </Typography>
          <Typography variant="caption" color="error" sx={{ mt: 1.5, display: 'block' }}>
            Categories that still have subcategories cannot be deleted until those are removed or moved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteCategory} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default CategoriesPage;
